import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Check, FileText, Plus, Trash2, Download, Loader2, PartyPopper } from 'lucide-react';
import '../styles/pricing.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = 'https://customer-assets-lqy194kg.emergentagent.net/job_31bcde7c-d98a-4c27-a3ab-d96f08806001/artifacts/e3mvklhv_Logo%20Pink.png';
const gbp = (n) => `£${Math.round(n).toLocaleString('en-GB')}`;

// Turn a backend error into a friendly, human message (esp. Pydantic 422s).
function friendlyQuoteError(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    if (detail.some((d) => Array.isArray(d.loc) && d.loc.includes('email'))) {
      return 'That email address doesn’t look quite right — please double-check it.';
    }
    return 'Please check the highlighted details and try again.';
  }
  if (typeof detail === 'string') return detail;
  return 'Something went wrong creating your quote. Please try again.';
}

function computePreview(catalog, selectedIds, customLines) {
  if (!catalog) return { lines: [], oneoffEx: 0, monthlyEx: 0 };
  const byId = {};
  catalog.groups.forEach((g) => g.items.forEach((it) => { byId[it.id] = { ...it, category: g.category }; }));
  const lines = [];
  selectedIds.forEach((id) => {
    const it = byId[id];
    if (!it) return;
    lines.push({ label: it.name, category: it.category, unit: it.unit, total: it.price_ex });
    if (it.setup_ex) {
      const setupLabel = it.category === 'Paid Media' ? 'onboarding' : 'setup';
      lines.push({ label: `${it.name} — ${setupLabel}`, category: it.category, unit: 'one-off', total: it.setup_ex, isSetup: true });
    }
  });
  const hasWebsite = lines.some((l) => l.category === 'Website Design' && l.unit === 'one-off');
  if (hasWebsite) {
    lines.forEach((l) => {
      if (l.category === 'SEO + GEO Growth' && l.unit === 'one-off' && l.total > 0) {
        l.label += ' (waived — bundle offer)';
        l.total = 0;
        l.waived = true;
      }
    });
  }
  customLines.forEach((c) => {
    const price = parseFloat(c.price) || 0;
    lines.push({ label: c.label || 'Custom item', category: 'Custom', unit: c.unit, total: price, custom: true });
  });
  const oneoffEx = lines.filter((l) => l.unit === 'one-off').reduce((s, l) => s + l.total, 0);
  const monthlyEx = lines.filter((l) => l.unit === 'monthly').reduce((s, l) => s + l.total, 0);
  return { lines, oneoffEx, monthlyEx };
}

export function QuoteForm({ adminToken = null, onCreated }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState([]);
  const [customLines, setCustomLines] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', business: '', phone: '', notes: '', consent: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing/catalog`).then((r) => setCatalog(r.data)).catch(() => setError('Could not load pricing. Please refresh.'));
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const preview = useMemo(() => computePreview(catalog, selected, customLines), [catalog, selected, customLines]);
  const oneoffInc = preview.oneoffEx * 1.2;
  const monthlyInc = preview.monthlyEx * 1.2;
  const hasSelection = selected.length > 0 || customLines.some((c) => c.label && c.price);

  const addCustom = () => setCustomLines((l) => [...l, { label: '', price: '', unit: 'one-off' }]);
  const updateCustom = (i, key, val) => setCustomLines((l) => l.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  const removeCustom = (i) => setCustomLines((l) => l.filter((_, idx) => idx !== i));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!hasSelection) { setError('Please select at least one service.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Please enter a valid email address so we can send your quote.'); return;
    }
    if (!adminToken && !form.consent) { setError('Please agree to be contacted about your quote.'); return; }
    setBusy(true);
    try {
      const items = [
        ...selected.map((id) => ({ id, qty: 1 })),
        ...(adminToken ? customLines.filter((c) => c.label && c.price).map((c) => ({ label: c.label, price_ex: parseFloat(c.price), unit: c.unit, qty: 1 })) : []),
      ];
      const headers = { 'Content-Type': 'application/json' };
      if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
      const res = await axios.post(`${API}/quote`, { ...form, items }, { headers });
      setResult(res.data);
      if (onCreated) onCreated(res.data);
    } catch (err) {
      setError(friendlyQuoteError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null); setSelected([]); setCustomLines([]);
    setForm({ name: '', email: '', business: '', phone: '', notes: '', consent: false });
  };

  if (result) {
    return (
      <div className="qb-success" data-testid="quote-success">
        <span className="qb-success-icon"><PartyPopper size={30} /></span>
        <h2 className="pp-display">Quote {result.reference} is ready</h2>
        <p>
          {adminToken
            ? 'The quote has been created and emailed to the client. You can download the PDF below.'
            : "We've emailed a copy to you. You can download your PDF quote below."}
        </p>
        <div className="qb-success-totals">
          {result.subtotal_oneoff_ex > 0 && (
            <div><span>One-off</span><strong data-testid="quote-result-oneoff">{gbp(result.total_oneoff_inc)} inc VAT</strong></div>
          )}
          {result.subtotal_monthly_ex > 0 && (
            <div><span>Monthly</span><strong data-testid="quote-result-monthly">{gbp(result.total_monthly_inc)} inc VAT / mo</strong></div>
          )}
        </div>
        <div className="qb-success-btns">
          <a className="pp-btn pp-btn-primary" href={`${API}/quote/${result.id}/pdf`} target="_blank" rel="noreferrer" data-testid="quote-download-pdf">
            <Download size={16} /> Download PDF quote
          </a>
          <button className="pp-btn pp-btn-ghost" onClick={reset} data-testid="quote-new">Build another quote</button>
        </div>
      </div>
    );
  }

  return (
    <div className="qb-grid" data-testid="quote-form">
      <div className="qb-select">
        {!catalog && <p className="pp-lead">Loading services…</p>}
        {catalog && catalog.groups.map((g) => (
          <div className="qb-group" key={g.category}>
            <span className="qb-group-label">{g.category}</span>
            <div className="qb-items">
              {g.items.map((it) => {
                const on = selected.includes(it.id);
                return (
                  <button
                    type="button"
                    key={it.id}
                    className={`qb-item ${on ? 'qb-item-on' : ''}`}
                    onClick={() => toggle(it.id)}
                    data-testid={`quote-item-${it.id}`}
                  >
                    <span className="qb-item-check">{on && <Check size={14} />}</span>
                    <span className="qb-item-body">
                      <span className="qb-item-name">{it.name}</span>
                      <span className="qb-item-desc">{it.desc}</span>
                    </span>
                    <span className="qb-item-price">
                      {gbp(it.price_ex)}<small>{it.unit === 'monthly' ? ' + VAT / mo' : ' + VAT'}</small>
                      {it.setup_ex ? <em>+ {gbp(it.setup_ex)} setup</em> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {adminToken && (
          <div className="qb-group" data-testid="quote-custom-lines">
            <span className="qb-group-label">Custom line items (admin)</span>
            {customLines.map((c, i) => (
              <div className="qb-custom-row" key={i}>
                <input placeholder="Description" value={c.label} onChange={(e) => updateCustom(i, 'label', e.target.value)} data-testid={`quote-custom-label-${i}`} />
                <input placeholder="£ ex VAT" type="number" value={c.price} onChange={(e) => updateCustom(i, 'price', e.target.value)} data-testid={`quote-custom-price-${i}`} />
                <select value={c.unit} onChange={(e) => updateCustom(i, 'unit', e.target.value)} data-testid={`quote-custom-unit-${i}`}>
                  <option value="one-off">One-off</option>
                  <option value="monthly">Monthly</option>
                </select>
                <button type="button" className="qb-custom-del" onClick={() => removeCustom(i)} aria-label="Remove line"><Trash2 size={15} /></button>
              </div>
            ))}
            <button type="button" className="pp-btn pp-btn-ghost pp-btn-sm" onClick={addCustom} data-testid="quote-add-custom">
              <Plus size={15} /> Add custom line
            </button>
          </div>
        )}
      </div>

      <form className="qb-summary" onSubmit={submit}>
        <span className="qb-summary-kicker"><FileText size={15} /> Your quote</span>
        <div className="qb-lines" data-testid="quote-preview-lines">
          {preview.lines.length === 0 && <p className="qb-empty">Select services to build your quote.</p>}
          {preview.lines.map((l, i) => (
            <div className="qb-line" key={i}>
              <span>{l.label}</span>
              <span className={l.waived ? 'qb-waived' : ''}>
                {l.waived ? '£0' : gbp(l.total)}{l.unit === 'monthly' ? ' /mo' : ''}
              </span>
            </div>
          ))}
        </div>
        {(preview.oneoffEx > 0 || preview.monthlyEx > 0) && (
          <div className="qb-totals">
            {preview.oneoffEx > 0 && (
              <div><span>One-off (inc VAT)</span><strong data-testid="quote-total-oneoff">{gbp(oneoffInc)}</strong></div>
            )}
            {preview.monthlyEx > 0 && (
              <div><span>Monthly (inc VAT)</span><strong data-testid="quote-total-monthly">{gbp(monthlyInc)} /mo</strong></div>
            )}
          </div>
        )}

        <div className="qb-fields">
          <input required placeholder="Your name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-name" />
          <div className={`pp-input-wrap ${emailValid ? 'is-valid' : ''}`}>
            <input required type="email" placeholder="Email*" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email" />
            {emailValid && <Check size={18} strokeWidth={3} className="pp-valid-tick" data-testid="quote-email-valid" />}
          </div>
          <input placeholder="Business name" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} data-testid="quote-business" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-phone" />
          <textarea placeholder="Anything else we should know? (optional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-notes" />
          {!adminToken && (
            <label className="qb-consent">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="quote-consent" />
              <span>I agree that Boost! may contact me about this quote.</span>
            </label>
          )}
        </div>

        {error && <p className="ed-action-error" role="alert" data-testid="quote-error">{error}</p>}
        <button type="submit" className="pp-btn pp-btn-primary" style={{ width: '100%' }} disabled={busy} data-testid="quote-submit">
          {busy ? <><Loader2 size={16} className="qb-spin" /> Generating…</> : (adminToken ? 'Create & send quote' : 'Get my instant quote')}
        </button>
        <p className="qb-note">Estimate valid 30 days. Every project is individually scoped and confirmed in a written proposal.</p>
      </form>
    </div>
  );
}

export default function QuoteBuilder() {
  useEffect(() => { window.scrollTo(0, 0); document.title = 'Instant Quote Builder | Boost!'; }, []);
  return (
    <div className="pricing-page" data-testid="quote-builder-page">
      <header className="pp-header">
        <div className="pp-header-inner">
          <Link to="/" aria-label="BOOST! Design & Marketing home"><img src={LOGO} alt="Boost! Design & Marketing" /></Link>
          <div className="pp-header-links">
            <Link to="/" className="pp-header-link">Main site</Link>
            <Link to="/website-design-pricing" className="pp-header-link" data-testid="quote-pricing-link">Pricing</Link>
          </div>
        </div>
      </header>
      <section className="pp-hero" style={{ paddingBottom: 8 }}>
        <div className="pp-container">
          <span className="pp-kicker">Instant Quote Builder</span>
          <h1 className="pp-display" data-testid="quote-h1">BUILD YOUR <em>QUOTE</em> IN SECONDS.</h1>
          <p className="pp-hero-copy">
            Choose the services you're interested in and we'll instantly generate a branded PDF quote,
            email it to you and calculate your totals — including VAT and any bundle savings.
          </p>
        </div>
      </section>
      <section className="pp-section" style={{ paddingTop: 12 }}>
        <div className="pp-container">
          <QuoteForm />
        </div>
      </section>
    </div>
  );
}
